import { Command } from '@dreryk/command-query-api';

import { Bean, createTranslatedText, PromiseVoidFunction, TranslatableText, Translate } from '@dreryk/react-common';
import { EditIcon, Link, List, MultilineTextOverflow, StyledFlex, TestId } from '@dreryk/react-components';

import { ExtractItemId } from '../FormsTemplates.types';
import { useFormsTemplatesContext } from '../formsTemplatesContext';
import { formsTemplatesTranslationKeys } from '../translations';

import { FormsTemplatesFormLeavingEventInterceptor } from './FormsTemplatesFormLeavingEventInterceptor';
import { StyledFormsTemplatesListItem } from './FormsTemplatesListItem.styled';
import { StyledFormsTemplatesListItemActiveNameMultilineTextOverflow } from './FormsTemplatesListItemActiveNameMultilineTextOverflow.styled';
import { StyledFormsTemplatesListItemItemName } from './FormsTemplatesListItemItemName.styled';
import { StyledFormsTemplatesListItemPlaceholderName } from './FormsTemplatesListItemPlaceholderName.styled';
import { EMPTY_TEMPLATE_ID } from './FormsTemplatesListItemsRenderer';
import { FormsTemplatesListRemoveTemplate } from './FormsTemplatesListRemoveTemplate';

type WithActionItems<ITEM extends Bean> = {
   displayActionItems: true;
   templateId: ExtractItemId<ITEM>;
};
type WithoutActionItems<ITEM extends Bean> = {
   displayActionItems: false;
   templateId: ExtractItemId<ITEM> | typeof EMPTY_TEMPLATE_ID;
};

type FormsTemplatesListItemProps<ITEM extends Bean> = {
   readonly onClick: VoidFunction | undefined;
   readonly active: boolean;
   readonly testId: TestId;
   readonly onEditIconClick: VoidFunction;
   readonly displayedName: string | undefined;
   readonly onTemplateRemoved: PromiseVoidFunction;
   readonly removeConfirmationTitle: TranslatableText;
   readonly removeSuccessMessage: TranslatableText;
   readonly removeCommandCreator: <RESULT>(templateId: ExtractItemId<ITEM>) => Command<RESULT>;
} & (WithActionItems<ITEM> | WithoutActionItems<ITEM>);

export const FormsTemplatesListItem = <ITEM extends Bean>(props: FormsTemplatesListItemProps<ITEM>): JSX.Element => {
   const { switchingDisabled } = useFormsTemplatesContext();

   return (
      <List.Item testId={props.testId}>
         <StyledFormsTemplatesListItem justifyContent={'space-between'} alignItems={'center'} gap={'8px'}>
            <FormsTemplatesFormLeavingEventInterceptor active={switchingDisabled}>
               <StyledFormsTemplatesListItemItemName alignItems={'center'} onClick={props.onClick}>
                  {props.active && props.displayedName ? (
                     <StyledFormsTemplatesListItemActiveNameMultilineTextOverflow
                        text={createTranslatedText(props.displayedName)}
                        lines={2}
                        testId={'activeTemplate'}
                     />
                  ) : null}
                  {props.displayedName && !props.active ? (
                     <MultilineTextOverflow
                        text={createTranslatedText(props.displayedName)}
                        lines={2}
                        testId={'templateName'}
                     />
                  ) : null}
                  {props.displayedName ? null : (
                     <StyledFormsTemplatesListItemPlaceholderName>
                        <Translate>{formsTemplatesTranslationKeys.templateName}</Translate>
                     </StyledFormsTemplatesListItemPlaceholderName>
                  )}
               </StyledFormsTemplatesListItemItemName>
            </FormsTemplatesFormLeavingEventInterceptor>
            {props.displayActionItems ? (
               <StyledFlex alignItems={'center'}>
                  <Link onClick={props.onEditIconClick} icon={<EditIcon />} testId={'updateTemplate'} />
                  <FormsTemplatesListRemoveTemplate<ITEM>
                     templateId={props.templateId}
                     templateName={props.displayedName!}
                     onSuccess={props.onTemplateRemoved}
                     confirmationModalTitle={props.removeConfirmationTitle}
                     successMessage={props.removeSuccessMessage}
                     commandCreator={props.removeCommandCreator}
                  />
               </StyledFlex>
            ) : null}
         </StyledFormsTemplatesListItem>
      </List.Item>
   );
};
